import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Row, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { AssetMaintenancePhotos } from './AssetMaintenancePhotos';
import { AssetMaintenanceRequestHistory } from './AssetMaintenanceRequestHistory';
import { ApprovalDecisionActions } from '../../_components/Shared/ApprovalDecisionActions';
import './AssetMaintenanceRecord.css';

export const REPAIR_CATEGORIES = [
    'Functional Issue',
    'Mechanical Failure',
    'Modification (ECO)',
    'Wear & Tear',
    'Total Preventive Maintenance',
    'Others',
];

// Mobile stores selected categories separately and prefixes each manual description.
// Only recognize that complete format; keep older or ambiguous text intact.
export function repairReasonRows(record) {
    const selected = String(record.reasonCategory || '').split(', ').filter(Boolean);
    const description = String(record.problemDescription || '');
    const recognized = selected.length > 0 &&
        record.reason === record.reasonCategory &&
        selected.every((label) => REPAIR_CATEGORIES.includes(label)) &&
        new Set(selected).size === selected.length;
    const issues = {};
    let remainder = description;

    if (recognized) {
        for (let index = 0; index < selected.length; index += 1) {
            const prefix = `${selected[index]}: `;
            if (!remainder.startsWith(prefix)) return { issues: {}, legacy: true };

            const nextPrefix = index + 1 < selected.length ? `\n${selected[index + 1]}: ` : null;
            const end = nextPrefix ? remainder.indexOf(nextPrefix) : remainder.length;
            if (end < 0 || (nextPrefix && remainder.indexOf(nextPrefix, end + 1) >= 0)) {
                return { issues: {}, legacy: true };
            }

            issues[selected[index]] = remainder.slice(prefix.length, end);
            remainder = nextPrefix ? remainder.slice(end + 1) : '';
        }
    }

    return { issues, legacy: !recognized };
}

function timestamp(value) {
    if (!value || !Number.isFinite(Date.parse(value))) return '—';
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kuala_Lumpur',
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(new Date(value));
}

function InfoCell({ label, value }) {
    return (
        <Col md={6} className="mb-2">
            <div className="maintenance-info-cell">
                <strong>{label}: </strong>{value || '—'}
            </div>
        </Col>
    );
}

export function AssetMaintenanceRecord({ detail, kind, title, base, onDecide, onReload }) {
    const [record, setRecord] = useState(detail);
    const [reloadBusy, setReloadBusy] = useState(false);
    const [decisionBusy, setDecisionBusy] = useState(false);
    const [error, setError] = useState(null);
    const inFlight = useRef(false);
    const busy = reloadBusy || decisionBusy;
    const reasons = repairReasonRows(record);
    const asset = record.asset || {};
    const number = asset.machine || asset.jigFixNo || asset.testerNo;
    const assetLabel = kind === 'machines' ? 'Machine' : kind === 'testers' ? 'Tester' : 'Jig & Fixture';
    const process = record.processDetails || {};
    const rootCauses = process.rootCauses || [];
    const repairActions = process.repairActions?.length ? process.repairActions : record.repairActions || [];
    const verificationChecks = process.verificationChecks || [];
    const repairCost = repairActions.reduce((total, action) => total + Number(action.cost || 0), 0);

    useEffect(() => {
        setRecord(detail);
    }, [detail]);

    async function reload() {
        if (inFlight.current || decisionBusy) return;

        inFlight.current = true;
        setReloadBusy(true);
        setError(null);
        try {
            setRecord(await onReload());
        } catch (failure) {
            setError(failure);
        } finally {
            inFlight.current = false;
            setReloadBusy(false);
        }
    }

    function photos(section) {
        const ids = (record.evidence?.[section] || []).map((item) =>
            typeof item === 'string' ? item : item.id
        );
        if (!ids.length) return null;

        return (
            <AssetMaintenancePhotos
                kind={kind}
                parentType="request"
                parentId={record.id}
                section={section}
                attachmentIds={ids}
                readOnly
            />
        );
    }

    return (
        <>
            <div className="maintenance-record-toolbar d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <Link className="btn btn-outline-secondary" to={base}>Back</Link>
                <div className="d-flex align-items-center flex-wrap gap-2">
                    <strong className="text-secondary">{record.processStatus}</strong>
                    <ApprovalDecisionActions
                        recordType="machining_maintenance"
                        recordId={record.id}
                        recordLabel={record.ticketId}
                        currentLevel={record.approval?.currentLevel}
                        canApprove={Boolean(record.approval?.canApprove)}
                        canReject={Boolean(record.approval?.canApprove)}
                        disabled={reloadBusy || Boolean(error)}
                        submitDecision={onDecide}
                        onDecision={async () => setRecord(await onReload())}
                        onError={setError}
                        onBusyChange={setDecisionBusy}
                    />
                    <Button variant="secondary" onClick={() => window.print()}>Print</Button>
                </div>
            </div>
            {error && (
                <Alert variant="danger" className="maintenance-record-toolbar">
                    {error.message}{' '}
                    <Button variant="outline-danger" disabled={busy} onClick={reload}>
                        Reload latest
                    </Button>
                </Alert>
            )}
            <article id="asset-maintenance-record" className="maintenance-record">
                <header className="text-center mb-3">
                    <h3>{title} Maintenance &amp; Repair Record</h3>
                    <p className="text-muted">Integrated request, maintenance action execution, and verification workflow.</p>
                </header>
                <h5>1. {title} Maintenance Request</h5>
                <Row>
                    <InfoCell label="Repair Ticket ID" value={record.ticketId} />
                    <InfoCell label={`${assetLabel} Name`} value={asset.name} />
                    <InfoCell label="Asset No." value={asset.assetNo} />
                    <InfoCell label={`${assetLabel} No.`} value={number} />
                    <InfoCell label={`${assetLabel} Model`} value={record.assetModel ?? asset.model} />
                    <InfoCell label="Event type" value={record.eventType} />
                    <InfoCell label="Date/Time (Malaysia)" value={timestamp(record.requestedAt || record.reportedAt)} />
                    <InfoCell label="Reported By" value={record.reportedByUsername || (record.reportedBy != null ? `User ${record.reportedBy}` : null)} />
                </Row>
                <h5 className="mt-3">2. Problem Description &amp; Reason for Repair</h5>
                <Table bordered size="sm" responsive>
                    <thead><tr><th>Active</th><th>Reason / Category</th><th>Specific Issue</th></tr></thead>
                    <tbody>
                        {REPAIR_CATEGORIES.map((label) => (
                            <tr key={label}>
                                <td className="text-center">
                                    {Object.prototype.hasOwnProperty.call(reasons.issues, label) ? 'X' : ''}
                                </td>
                                <td>{label}</td>
                                <td className="maintenance-text">{reasons.issues[label] || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                {reasons.legacy && <section aria-label="Original request text">
                    <h6>Original request text</h6>
                    <dl>
                        {[
                            ['Reason', record.reason],
                            ['Reason category', record.reasonCategory],
                            ['Problem description', record.problemDescription],
                        ].map(([label, value]) => value ? (
                            <React.Fragment key={label}>
                                <dt>{label}</dt>
                                <dd className="maintenance-text">{value}</dd>
                            </React.Fragment>
                        ) : null)}
                    </dl>
                </section>}
                {photos('problem')}
                <h5 className="mt-3">3. Root Cause Analysis</h5>
                <Table bordered size="sm" responsive>
                    <thead><tr><th>Active</th><th>Root Cause</th><th>Detail</th></tr></thead>
                    <tbody>
                        {rootCauses.length ? rootCauses.map((cause, index) => (
                            <tr key={index}>
                                <td className="text-center">X</td>
                                <td className="maintenance-text">{cause.category}</td>
                                <td className="maintenance-text">{cause.detail || '—'}</td>
                            </tr>
                        )) : (
                            <tr>
                                <td />
                                <td className="maintenance-text">{record.rootCause || '—'}</td>
                                <td>—</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
                <h5 className="mt-3">4. {title} Maintenance Done</h5>
                <Row>
                    <InfoCell label="Technician In-Charge" value={process.technician || '—'} />
                    <InfoCell label="Date & Time Done (Malaysia)" value={timestamp(record.submittedAt)} />
                    {[
                        ['Repair started', record.repairStartedAt],
                        ['Repair completed', record.repairCompletedAt],
                        ['Downtime started', record.downtimeStartedAt],
                        ['Returned to service', record.returnedToServiceAt],
                    ].map(([label, value]) => (
                        <InfoCell key={label} label={label} value={timestamp(value)} />
                    ))}
                </Row>
                <Table bordered size="sm" responsive>
                    <thead>
                        <tr>
                            <th>Component / Area</th>
                            <th>Action Taken</th>
                            <th>Material / Part Used</th>
                            <th>Qty</th>
                            <th>Cost (MYR)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {repairActions.map((action, index) => (
                            <tr key={index}>
                                <td className="maintenance-text">{action.component || '—'}</td>
                                <td className="maintenance-text">{action.action || action.description || '—'}</td>
                                <td className="maintenance-text">{action.materialPartsUsed || '—'}</td>
                                <td className="text-end">{action.quantity ?? '—'}</td>
                                <td className="text-end">{Number(action.cost || 0).toFixed(2)}</td>
                            </tr>
                        ))}
                        {!repairActions.length && (
                            <tr><td colSpan={5}>No repair actions recorded.</td></tr>
                        )}
                        {repairActions.length > 0 && (
                            <tr>
                                <th colSpan={4} className="text-end">Total Spares Cost:</th>
                                <td className="text-end fw-bold">{repairCost.toFixed(2)}</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
                <div className="maintenance-info-cell maintenance-text mb-3">
                    <strong>Technical Notes: </strong>{record.technicalNotes || '—'}
                </div>
                <h5 className="mt-3">5. Verification &amp; Sign-Off</h5>
                <Table bordered size="sm" responsive>
                    <thead>
                        <tr>
                            <th>Mandatory Verification Checkpoint</th>
                            <th>Status</th>
                            <th>Comments / Measurements</th>
                        </tr>
                    </thead>
                    <tbody>
                        {verificationChecks.length ? verificationChecks.map((check, index) => (
                            <tr key={index}>
                                <td className="maintenance-text">{check.checkpoint}</td>
                                <td>{check.result || '—'}</td>
                                <td className="maintenance-text">{check.remarks || '—'}</td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={3} className="maintenance-text">{record.verification || '—'}</td>
                            </tr>
                        )}
                    </tbody>
                </Table>
                <Table bordered size="sm">
                    <tbody>
                        {[
                            ['Final condition', record.finalCondition],
                            ['Next PM information', record.nextPmInformation],
                            ['Approval cycle', record.approvalCycle],
                            ['Submitted at (Malaysia)', timestamp(record.submittedAt)],
                            ['Approved at (Malaysia)', timestamp(record.approvedAt)],
                        ].map(([label, value]) => (
                            <tr key={label}>
                                <th>{label}</th>
                                <td className="maintenance-text">{value === 0 ? 0 : value || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                {photos('verification')}
                <AssetMaintenanceRequestHistory events={record.audit || []} />
            </article>
        </>
    );
}
